import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useAdobeClientId } from 'vl-common/src/hooks/useAdobeClientId';
import { breakpoints } from '../../lib/breakpoints';
import ReactCrop from 'react-image-crop';
import { InfoModal } from 'vl-common/src/components/Modals';
import 'react-image-crop/dist/ReactCrop.css';
import { useQuery } from '@tanstack/react-query';
import { FileWithContent, FileWithoutContent } from '.';

const StyledInfoModal = styled(InfoModal)`
  width: 100% !important;
  height: 90%;

  &.image-info-modal {
    .ant-modal-body {
      height: 84%;
      text-align: center;

      & > img {
        object-fit: contain;
        height: 100%;
        width: 100%;
      }
    }
  }

  // mobile
  @media (max-width: ${breakpoints.md}px) {
    height: 65%;
  }

  // desktops
  @media (min-width: ${breakpoints.xxl}px) {
    width: 85% !important;
    height: auto; !important;
  }

  .ant-modal-content {
    display: flex;
    flex-direction: column;
    padding: 32px;
    height: calc(100vh - 64px);
  }

  .ant-modal-header {
    border-bottom: none;
    padding: 0;
    margin-bottom: 8px;
  }

  .ant-modal-body {
    padding: 0;
    margin-bottom: 24px;
    flex-grow: 1;
  }

  .ant-modal-footer {
    border: none !important;
    padding: 0;
  }
`;

const isPdfFileExtension = (filename: string | undefined): boolean => {
  if (!filename) {
    return false;
  }
  return filename.toLowerCase().endsWith('.pdf');
};

const PDFViewer = ({ url, fileName }: { url: string; fileName: string }) => {
  const containerDiv = useRef<HTMLDivElement>(null);
  const clientId = useAdobeClientId();

  const AdobeDC = window?.AdobeDC;
  const divId = 'document-preview';

  useEffect(() => {
    const renderPdfViewer = async () => {
      if (!containerDiv.current) return;

      new AdobeDC.View({ clientId, divId }).previewFile(
        { content: { location: { url } }, metaData: { fileName } },
        {},
        containerDiv.current
      );
    };

    renderPdfViewer().then();
  }, []);

  return <div ref={containerDiv} id={divId} style={{ height: '100%' }} />;
};

const ImageViewer = ({ url, fileName }: { url: string; fileName: string }) => {
  return <img alt={fileName} src={url} />;
};

const CropViewer = ({ url, fileName, crop }: { url: string; fileName: string; crop: any }) => {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <ReactCrop className="no-dim-crop" crop={crop} disabled locked onChange={() => {}}>
        <img
          alt={fileName}
          src={url}
          style={{ maxHeight: '500px', height: '100%', maxWidth: '800px', width: '100%', objectFit: 'contain' }}
        />
      </ReactCrop>
    </div>
  );
};

const FileViewModal = ({
  open,
  value,
  onClose,
  downloadFileFromS3
}: {
  open: boolean;
  value: FileWithoutContent;
  onClose: () => void;
  downloadFileFromS3: (val: FileWithoutContent) => Promise<FileWithContent>;
}) => {
  const isPDF = isPdfFileExtension(value?.name);
  const hasCrop = !!value.crop;

  const { data, isLoading } = useQuery({
    queryKey: ['getFileFromS3', value?.name],
    queryFn: () => downloadFileFromS3(value),
    enabled: value !== undefined && !!value.name
  });

  return (
    <StyledInfoModal
      open={open}
      title={`Preview of ${value?.name}`}
      onCancel={onClose}
      onOk={onClose}
      closable={true}
      footer={null}
      className={isPDF ? '' : 'image-info-modal'}
    >
      {isLoading && 'Loading file...'}
      {!isLoading && !data?.content && 'Unable to preview file'}
      {!isLoading && data?.content && (
        <>
          {isPDF && <PDFViewer url={data.content} fileName={value?.name} />}
          {hasCrop && <CropViewer fileName={value?.name} url={data.content} crop={value.crop} />}
          {!isPDF && !hasCrop && <ImageViewer fileName={value?.name} url={data.content} />}
        </>
      )}
    </StyledInfoModal>
  );
};

export default FileViewModal;
